import { css } from 'aphrodite';
import { FC, useEffect } from 'react';
import { CustomPageType } from '../../../extViewmodels';
import { useHostedBlogPagePreviewQuery, useSurveyPreviewQuery } from '../../../queries';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { bs } from '../../../web/styles/styles';
import { HostedBlogPreview } from '../../containers/blogs/BlogEditor/HostedBlogPreview';
import { PreviewContext } from '../../containers/blogs/TemplateDesigner/PreviewContextTabs';
import { Column, Row } from '../Layout';
import { useAccountContext } from './AccountContext';
import { Divider } from './presentation';
import { styleSheet } from './styles';

interface HtmlPreviewFrameProps {
	type: CustomPageType;
	refreshPreview?: boolean;
	previewContext?: Exclude<PreviewContext, 'meeting'>;
}

export const HtmlPreviewFrame: FC<HtmlPreviewFrameProps> = ({ refreshPreview, type, previewContext = 'blog' }) => {
	const { impersonationContext } = useAccountContext();

	const blogQuery = useHostedBlogPagePreviewQuery({
		page: type,
		impersonationContext,
		refetchOnWindowFocus: false,
		enabled: previewContext === 'blog',
	});

	const surveyQuery = useSurveyPreviewQuery({
		impersonationContext,
		enabled: previewContext === 'survey',
	});

	const activeQuery = previewContext === 'survey' ? surveyQuery : blogQuery;

	const { data: html, isPending, refetch } = activeQuery;

	useEffect(() => {
		if (refreshPreview) {
			refetch();
		}
	});

	const { Footer, Header, BlogPost, BlogHome } = CustomPageType;

	let title: string;

	if (previewContext === 'survey') {
		title = 'Survey Page';
	} else {
		switch (type) {
			case BlogPost:
				title = 'Blog Post';
				break;
			case BlogHome:
				title = 'Home Page';
				break;
			case Header:
				title = 'Header';
				break;
			case Footer:
				title = 'Footer';
				break;
			default:
				title = 'Section';
				break;
		}
	}

	return (
		<Column className={css(styleSheet.gap12)}>
			<h2 className={css(styleSheet.subtitleText)}>{`${title} Preview`}</h2>
			<Divider />
			{isPending && (
				<Row className={css(bs.p7, bs.justifyCenter)}>
					<LoadingSpinner />
				</Row>
			)}
			{!!html && !isPending && (
				<HostedBlogPreview
					className={css(styleSheet.previewFrame)}
					htmlContent={html}
					disableClicks
					scrolling='no'
					fullHeight
				/>
			)}
		</Column>
	);
};
